import { t } from '@/features/i18n/utils';
import { SuspenseBoundary } from '@/ui/components/suspense-boundary';
import { SupplementBreadcrumbs } from '@/features/supplements/components/supplement-breadcrumbs';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Icon } from '@/ui/components/icon';
import { FolderIcon, InformationCircleIcon } from '@hugeicons-pro/core-stroke-rounded';
import { Alert, AlertDescription, AlertTitle } from '@/ui/components/alert';
import { HeaderBackButton, HeaderChatButton, Header } from '@/ui/components/header';
import {
  MainAlert,
  MainContainer,
  MainFrames,
  MainHeader,
  MainNav,
  MainNavCompanyItem,
  MainNavItem,
  MainNavLink,
  MainNavList,
  MainScrollArea,
} from '@/ui/components/main';
import { H1, H2 } from '@/ui/components/heading';
import { entityDocumentsQuery } from '@/features/documents/queries';
import { IndexingStatusAlerts } from '@/features/documents/components/indexing-status-alerts';
import { useSuspenseQuery } from '@tanstack/react-query';
import { ChangeAssessmentsFrame } from '@/features/supplements/components/change-assessments-frame';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/contract-management/$supplementId/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { workspaceId, projectId, supplementId } = Route.useParams();

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/workspaces/$workspaceId"
          params={{ projectId, workspaceId }}
        />
        <SupplementBreadcrumbs />
        <HeaderChatButton />
      </Header>
      <MainScrollArea>
        <MainAlert>
          <IndexingStatusAlerts resourceId={supplementId} documentType="supplement" />
          <Alert variant="info">
            <Icon icon={InformationCircleIcon} />
            <AlertTitle>
              {t({
                en: 'How does contract management work?',
                nb: 'Hvordan fungerer kontraktstyring?',
              })}
            </AlertTitle>
            <AlertDescription>
              <ul className="list-disc space-y-1 mt-1 pl-4">
                <li>
                  {t({
                    en: 'Add all files relevant for contract management',
                    nb: 'Legg til alle filer som er relevante for kontraktstyring',
                  })}{' '}
                  <Link
                    to="/project/$projectId/workspaces/$workspaceId/contract-management/$supplementId/files"
                    params={{ projectId, workspaceId, supplementId }}
                    className="underline underline-offset-2"
                  >
                    {t({ en: 'here', nb: 'her' })}
                  </Link>{' '}
                  {t({
                    en: 'after the contract is signed.',
                    nb: 'etter at kontrakten er signert.',
                  })}
                </li>
                <li>
                  {t({
                    en: 'Each file will be dated automatically, so Volve can follow the timeline of correspondence.',
                    nb: 'Hver fil dateres automatisk, slik at Volve kan følge tidslinjen i korrespondansen.',
                  })}
                </li>
                <li>
                  {t({
                    en: 'The chat answers from the signed contract and everything you add.',
                    nb: 'Chatten svarer ut fra den signerte kontrakten og alt du legger til.',
                  })}
                </li>
              </ul>
            </AlertDescription>
          </Alert>
        </MainAlert>
        <MainHeader>
          <H1>{t({ en: 'Contract management', nb: 'Kontraktstyring' })}</H1>
        </MainHeader>
        <MainNav>
          <MainNavList>
            <MainNavCompanyItem />
            <MainNavItem>
              <MainNavLink
                to="/project/$projectId/workspaces/$workspaceId/contract-management/$supplementId/files"
                params={{ projectId, workspaceId, supplementId }}
              >
                <Icon icon={FolderIcon} />
                {t({ en: 'Files', nb: 'Filer' })}{' '}
                <SuspenseBoundary>
                  <SupplementFilesCount />
                </SuspenseBoundary>
              </MainNavLink>
            </MainNavItem>
          </MainNavList>
        </MainNav>
        <MainContainer>
          <H2 className="mb-3">{t({ en: 'Explore and analyze', nb: 'Utforsk og analyser' })}</H2>
          <MainFrames>
            <ChangeAssessmentsFrame />
          </MainFrames>
        </MainContainer>
      </MainScrollArea>
    </>
  );
}

function SupplementFilesCount() {
  const { supplementId } = Route.useParams();
  const { data: documents } = useSuspenseQuery(entityDocumentsQuery(supplementId, 'supplement'));
  if (!documents.length) return null;
  return `(${documents.length})`;
}
